// useScrollAnchor — auto-scroll-to-bottom behavior for the chat message list.
//
// Extracted verbatim from `AIChatBot.js` (the in-component auto-scroll effect)
// as part of the behavior-preserving refactor (issue #240). Owns the
// `messagesEndRef` sentinel ref and the effect that scrolls it into view
// whenever the message list changes:
//
//   useEffect(() => {
//     messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
//   }, [messages]);
//
// The effect dependency (`[messages]`), the optional-chaining guard, and the
// `{ behavior: 'smooth' }` scroll options are preserved exactly so the
// side-effect-parity baseline (auto-scroll fires on open and on new messages)
// stays green. The container attaches the returned `messagesEndRef` to the
// sentinel `<div ref={messagesEndRef} />` at the bottom of the list, exactly as
// the source did.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useRef, useEffect, useCallback } from 'react';

/**
 * Manages the messages-end ref and auto-scrolls it into view when `messages`
 * changes.
 *
 * @param {Array} messages - The chat message list; a change triggers the scroll
 *   (same dependency the source effect used).
 * @returns {{
 *   messagesEndRef: import('react').RefObject<HTMLElement>,
 *   scrollToBottom: () => void
 * }} The sentinel ref to attach at the end of the list and a manual scroll
 *   helper that performs the identical `scrollIntoView` call.
 */
export function useScrollAnchor(messages) {
  const messagesEndRef = useRef(null);

  // Smoothly scroll the sentinel into view. Preserves the source's exact call
  // (optional chaining + `{ behavior: 'smooth' }`).
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Auto-scroll — fires whenever the message list changes, matching the source
  // effect's `[messages]` dependency.
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  return { messagesEndRef, scrollToBottom };
}

export default useScrollAnchor;
